import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import '@/features/applications/student/StudentApplicationDetailPage.css';
import {
    Container,
    Spinner,
    Alert,
    Button,
    Card,
    Badge,
    Row,
    Col,
    Table,
    Form,
    Modal,
} from 'react-bootstrap';
import studentApplicationService from '@/shared/services/studentApplicationService';
import courseService from '@/shared/services/courseService';
import positionService from '@/shared/services/positionService';
import useTermNames from '@/shared/hooks/useTermNames';
import { pillFor } from '@/shared/utils/statusPills';
import StatusPill from '@/components/common/StatusPill';
import EmploymentAttestationSummary from '@/components/hiring/EmploymentAttestationSummary';
import RequirementAnswersSummary from '@/components/hiring/RequirementAnswersSummary';
import RejectStudentApplicationModal from '@/features/applications/student/RejectStudentApplicationModal';
import { formatAnswerDay, PROGRAM_NAMES } from '@/shared/utils/deadline';
import { useFeature } from '@/shared/contexts/FeatureFlagContext';
import SharedAnswersSummary from './blocks/SharedAnswersSummary';
import { getProgramDefinition } from './programs';

// The ranked positions a student chose, named from the program's own list
// (a coordinator sees every position of their program, closed ones included).
function PositionChoicesCard({ application }) {
    const [rows, setRows] = useState(null);
    const [failed, setFailed] = useState(false);
    const ids = application.positions || [];
    const answers = application.requirementAttestations || [];

    useEffect(() => {
        if (ids.length === 0) return undefined;
        let cancelled = false;
        positionService
            .listPositions({
                program: application.program,
                term: application.term,
            })
            .then((data) => !cancelled && setRows(data.positions || []))
            .catch(() => !cancelled && setFailed(true));
        return () => {
            cancelled = true;
        };
        // the list is read once per application
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [application._id]);

    const byId = new Map((rows || []).map((r) => [r.applicationId, r]));
    const nameOf = (id) => {
        const row = byId.get(id);
        if (row) {
            return `${row.subject} ${row.courseNumber}${row.section ? `, section ${row.section}` : ''}`;
        }
        if (failed) return 'Position name unavailable';
        return rows ? 'A position no longer listed' : 'Loading...';
    };

    return (
        <Card className="border-0 shadow-sm mb-4">
            <Card.Body className="py-2">
                <strong className="mb-2 d-block">Position choices</strong>
                {ids.length === 0 ? (
                    <p className="text-muted mb-0">No positions chosen.</p>
                ) : (
                    <ol className="list-unstyled mb-0">
                        {ids.map((id, i) => {
                            const row = byId.get(id);
                            return (
                                <li key={id} className="py-1">
                                    <Badge bg="secondary" pill className="me-2">
                                        {i + 1}
                                    </Badge>
                                    <strong>{nameOf(id)}</strong>
                                    {row?.title && (
                                        <span className="text-muted ms-2">
                                            {row.title}
                                        </span>
                                    )}
                                    {row?.instructorName && (
                                        <small className="text-muted d-block ms-4">
                                            {row.instructorName}
                                        </small>
                                    )}
                                    {answers.some(
                                        (entry) => entry.position === id
                                    ) && (
                                        <div className="small ms-4">
                                            <RequirementAnswersSummary
                                                answers={answers.filter(
                                                    (entry) =>
                                                        entry.position === id
                                                )}
                                                courseLabel={
                                                    row
                                                        ? `${row.subject} ${row.courseNumber}`
                                                        : 'this course'
                                                }
                                            />
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ol>
                )}
            </Card.Body>
        </Card>
    );
}

function StudentApplicationDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const goBack = () =>
        location.key !== 'default' ? navigate(-1) : navigate('/dashboard');
    const [application, setApplication] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pending, setPending] = useState(null);
    const [actionError, setActionError] = useState(null);
    const [courses, setCourses] = useState([]);
    const [selectedCourseId, setSelectedCourseId] = useState('');
    const [showOfferModal, setShowOfferModal] = useState(false);
    const [confirmingReject, setConfirmingReject] = useState(false);
    const termName = useTermNames([application?.term]);
    const hiringEnabled = useFeature('hiring');

    useEffect(() => {
        // Reset per-application selection state — this component doesn't
        // remount when navigating between two /student-applications/:id
        // URLs (only the id param changes), so a stale course selection
        // could otherwise carry over into a different application.
        setCourses([]);
        setSelectedCourseId('');

        const load = async () => {
            try {
                const data =
                    await studentApplicationService.getApplicationById(id);
                setApplication(data);
            } catch (_err) {
                setError('Failed to load application. Please try again.');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [id]);

    useEffect(() => {
        if (!application?.program || !application?.term) {
            return;
        }
        const load = async () => {
            try {
                const data = await courseService.getEligibleCourses(
                    application.program,
                    application.term
                );
                setCourses(Array.isArray(data) ? data : data.courses || []);
            } catch (_err) {
                setCourses([]);
            }
        };
        load();
    }, [application?.program, application?.term]);

    if (loading) {
        return (
            <Container className="py-5 text-center">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 text-muted">Loading application</p>
            </Container>
        );
    }

    if (error || !application) {
        return (
            <Container className="py-5">
                <Button variant="link" className="ps-0 mb-2" onClick={goBack}>
                    ← Back
                </Button>
                <Alert variant="danger">
                    {error || 'Application not found.'}
                </Alert>
            </Container>
        );
    }

    const hasCoursePreferences = application.coursePreferences?.length > 0;
    const showPositions =
        application.positions?.length > 0 ||
        (hiringEnabled && !hasCoursePreferences);
    const showLegacyPreferences = hasCoursePreferences || !showPositions;

    const handleStatusChange = async (status, courseId) => {
        try {
            setPending(status);
            setActionError(null);
            await studentApplicationService.updateStatus(id, {
                status,
                courseId,
                fromStatus: application.status,
            });
            setPending(null);
            goBack();
            return true;
        } catch (err) {
            setActionError(
                err.response?.data?.message ||
                    'Failed to update status. Please try again.'
            );
            setPending(null);
            // 409 means the status moved under us; reload so the buttons
            // match what can actually be done now
            if (err.response?.status === 409) {
                setShowOfferModal(false);
                studentApplicationService
                    .getApplicationById(id)
                    .then(setApplication)
                    .catch(() => {});
            }
            return false;
        }
    };

    const statusConfig = pillFor('studentApplication', application.status);

    // The server says which moves are allowed from the current status
    const isOffered = application.status === 'offered';
    // With hiring on, an LA application is acted on per position, so the
    // whole-application moves are hidden; only taking back an open legacy
    // offer remains.
    const decidedByPosition = hiringEnabled && application.program === 'la';
    const allowedStatusChanges = (
        application.allowedStatusChanges || []
    ).filter(
        (status) => !decidedByPosition || (isOffered && status === 'rejected')
    );
    const canMoveTo = (status) => allowedStatusChanges.includes(status);
    const definition = getProgramDefinition(application.program);
    const Summary = definition?.Summary;
    // The blocks the program's wizard asks, so a card is shown only for what
    // the program asked in its own place
    const hasBlock = (name) =>
        definition
            ? definition.steps.some((step) => step.blocks.includes(name))
            : true;
    // With hiring on, offers are made on a position. A stale response that
    // still lists 'offered' must not bring the button back.
    const canOffer = canMoveTo('offered') && !hiringEnabled;

    return (
        <Container>
            <Button variant="link" className="ps-0 mb-2" onClick={goBack}>
                ← Back
            </Button>
            <h1 className="mt-3">Student Application</h1>

            <Card className="border-0 shadow-sm mb-4">
                <Card.Body className="py-2">
                    <Row className="align-items-center">
                        <Col md={9}>
                            <strong className="mb-2 d-block">
                                Student Information
                            </strong>
                            <small className="text-muted d-block">Name</small>
                            <p>{application.student?.name || '—'}</p>
                            <small className="text-muted d-block">Email</small>
                            <p>{application.student?.email || '—'}</p>
                            {application.student?.studentId && (
                                <>
                                    <small className="text-muted d-block">
                                        Student ID
                                    </small>
                                    <p>{application.student.studentId}</p>
                                </>
                            )}
                            <small className="text-muted d-block">
                                Program
                            </small>
                            <p>{application.program?.toUpperCase() || '—'}</p>
                            <small className="text-muted d-block">Term</small>
                            <p>
                                {termName(application.term) || 'Not provided'}
                            </p>
                            {isOffered && application.offeredCourse && (
                                <>
                                    <small className="text-muted d-block">
                                        Offered Course
                                    </small>
                                    <p>
                                        {application.offeredCourse.subject}{' '}
                                        {application.offeredCourse.courseNumber}
                                        {application.offeredCourse.title &&
                                            ` — ${application.offeredCourse.title}`}
                                    </p>
                                </>
                            )}
                        </Col>
                        <Col md={3} className="text-md-end">
                            <p>
                                <StatusPill
                                    family="studentApplication"
                                    value={application.status}
                                />
                            </p>
                            {application.submittedAt && (
                                <small className="text-muted">
                                    Submitted{' '}
                                    {formatAnswerDay(application.submittedAt)}
                                </small>
                            )}
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <EmploymentAttestationSummary
                attestation={application.employmentAttestation}
                unmet={application.employmentUnmet}
            />

            <Card className="border-0 shadow-sm mb-4">
                <Card.Body className="py-2">
                    <strong className="mb-2 d-block">
                        Academic Background
                    </strong>
                    <small className="text-muted d-block">Major</small>
                    <p>{application.academicBackground?.major || '—'}</p>
                    {application.academicBackground?.secondMajor && (
                        <>
                            <small className="text-muted d-block">
                                Second major
                            </small>
                            <p>{application.academicBackground.secondMajor}</p>
                        </>
                    )}
                    {application.academicBackground?.minor && (
                        <>
                            <small className="text-muted d-block">Minor</small>
                            <p>{application.academicBackground.minor}</p>
                        </>
                    )}
                </Card.Body>
            </Card>

            {showPositions && <PositionChoicesCard application={application} />}

            {showLegacyPreferences && (
                <Card className="border-0 shadow-sm mb-4">
                    <Card.Body className="py-2">
                        <strong className="mb-2 d-block">
                            Course Preferences
                        </strong>
                        {application.coursePreferences?.length > 0 ? (
                            <Table size="sm" bordered responsive>
                                <thead className="bg-light">
                                    <tr>
                                        <th>Rank</th>
                                        <th>Course</th>
                                        <th>Title</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[...application.coursePreferences]
                                        .sort(
                                            (a, b) =>
                                                (a.rank || 0) - (b.rank || 0)
                                        )
                                        .map((pref, i) => (
                                            <tr key={i}>
                                                <td>#{pref.rank || i + 1}</td>
                                                <td>
                                                    <Badge bg="secondary">
                                                        {pref.course?.subject}{' '}
                                                        {
                                                            pref.course
                                                                ?.courseNumber
                                                        }
                                                    </Badge>
                                                </td>
                                                <td>
                                                    {pref.course?.title || '—'}
                                                </td>
                                            </tr>
                                        ))}
                                </tbody>
                            </Table>
                        ) : (
                            <p className="text-muted mb-0">
                                No course preferences specified.
                            </p>
                        )}
                    </Card.Body>
                </Card>
            )}

            {hasBlock('availability') && (
                <Card className="border-0 shadow-sm mb-4">
                    <Card.Body className="py-2">
                        <strong className="mb-2 d-block">Availability</strong>
                        {application.availability?.hoursPerWeek != null && (
                            <>
                                <small className="text-muted d-block">
                                    Hours per week
                                </small>
                                <p>{application.availability.hoursPerWeek}</p>
                            </>
                        )}
                        {application.availability?.schedule?.length > 0 ? (
                            <Table size="sm" bordered responsive>
                                <thead className="bg-light">
                                    <tr>
                                        <th>Day</th>
                                        <th>Start Time</th>
                                        <th>End Time</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {application.availability.schedule.map(
                                        (slot, i) => (
                                            <tr key={i}>
                                                <td>{slot.day || '—'}</td>
                                                <td>{slot.startTime || '—'}</td>
                                                <td>{slot.endTime || '—'}</td>
                                            </tr>
                                        )
                                    )}
                                </tbody>
                            </Table>
                        ) : (
                            <p className="text-muted mb-0">
                                No schedule slots specified.
                            </p>
                        )}
                    </Card.Body>
                </Card>
            )}

            {hasBlock('interestStatement') && application.interestStatement && (
                <Card className="border-0 shadow-sm mb-4">
                    <Card.Body className="py-2">
                        <strong className="mb-2 d-block">
                            Interest Statement
                        </strong>
                        <p className="mb-0 text-pre-line">
                            {application.interestStatement}
                        </p>
                    </Card.Body>
                </Card>
            )}

            {hasBlock('priorExperience') && application.priorExperience && (
                <Card className="border-0 shadow-sm mb-4">
                    <Card.Body className="py-2">
                        <strong className="mb-2 d-block">
                            Prior Experience
                        </strong>
                        <p className="mb-0 text-pre-line">
                            {application.priorExperience}
                        </p>
                    </Card.Body>
                </Card>
            )}

            {application.references?.length > 0 && (
                <Card className="border-0 shadow-sm mb-4">
                    <Card.Body className="py-2">
                        <strong className="mb-2 d-block">References</strong>
                        <Table size="sm" bordered responsive>
                            <thead className="bg-light">
                                <tr>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Relationship</th>
                                    <th>Form Submitted</th>
                                </tr>
                            </thead>
                            <tbody>
                                {application.references.map((ref, i) => (
                                    <tr key={i}>
                                        <td>{ref.name || '—'}</td>
                                        <td>{ref.email || '—'}</td>
                                        <td>{ref.relationship || '—'}</td>
                                        <td>
                                            {ref.formSubmitted ? 'Yes' : 'No'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            )}

            <SharedAnswersSummary
                application={{
                    ...application,
                    availability: hasBlock('availability')
                        ? undefined
                        : application.availability,
                }}
                audience="coordinator"
            />

            {Summary && (
                <Summary application={application} audience="coordinator" />
            )}

            {actionError && (
                <Alert
                    variant="danger"
                    dismissible
                    onClose={() => setActionError(null)}
                >
                    {actionError}
                </Alert>
            )}

            <Row className="mt-3 mb-4">
                <Col className="d-flex justify-content-start gap-2">
                    {allowedStatusChanges.length === 0 && (
                        <p className="text-muted mb-0">
                            {decidedByPosition &&
                            application.allowedStatusChanges?.length > 0
                                ? "Learning Assistant applications are reviewed position by position. Open the position's candidates on the Positions tab to act on this applicant."
                                : `This application is ${statusConfig.label.toLowerCase()}, so its status can't be changed.`}
                        </p>
                    )}
                    {canMoveTo('under_review') && (
                        <Button
                            variant="outline-warning"
                            onClick={() => handleStatusChange('under_review')}
                            disabled={pending !== null}
                        >
                            {pending === 'under_review'
                                ? 'Updating...'
                                : 'Under Review'}
                        </Button>
                    )}
                    {canMoveTo('interview_scheduled') && (
                        <Button
                            variant="outline-primary"
                            onClick={() =>
                                handleStatusChange('interview_scheduled')
                            }
                            disabled={pending !== null}
                        >
                            {pending === 'interview_scheduled'
                                ? 'Updating...'
                                : 'Schedule Interview'}
                        </Button>
                    )}
                    {canMoveTo('rejected') && (
                        <Button
                            variant="danger"
                            onClick={() => setConfirmingReject(true)}
                            disabled={pending !== null}
                        >
                            {pending === 'rejected'
                                ? 'Updating...'
                                : isOffered
                                  ? 'Take back offer'
                                  : 'Reject'}
                        </Button>
                    )}
                    {canOffer && (
                        <Button
                            variant="success"
                            onClick={() => {
                                // Changing an offer starts from the course
                                // offered now. A new offer defaults to the
                                // student's top course preference, if they
                                // have one and it's still in the eligible-
                                // courses list — saves a click for the common
                                // case where that's also the actual placement.
                                // The coordinator can still pick any other
                                // eligible course instead.
                                const topPreference = [
                                    ...(application.coursePreferences || []),
                                ].sort(
                                    (a, b) => (a.rank || 0) - (b.rank || 0)
                                )[0];
                                const preferredCourseId = isOffered
                                    ? application.offeredCourse?._id
                                    : topPreference?.course?._id;
                                if (
                                    preferredCourseId &&
                                    courses.some(
                                        (c) => c._id === preferredCourseId
                                    )
                                ) {
                                    setSelectedCourseId(preferredCourseId);
                                }
                                setShowOfferModal(true);
                            }}
                            disabled={pending !== null}
                        >
                            {pending === 'offered'
                                ? 'Updating...'
                                : isOffered
                                  ? 'Change Offer'
                                  : 'Extend Offer'}
                        </Button>
                    )}
                </Col>
            </Row>
            {hiringEnabled &&
                (isOffered || allowedStatusChanges.length > 0) && (
                    <p className="text-body-secondary small">
                        {isOffered
                            ? 'This offer was made before offers moved to positions. It can be taken back here, but not changed.'
                            : 'Offers are made on each position now, from the Positions tab.'}
                    </p>
                )}

            <Modal
                aria-labelledby="sadp-modal-title-1"
                show={showOfferModal && !hiringEnabled}
                onHide={() => setShowOfferModal(false)}
            >
                <Modal.Header closeButton>
                    <Modal.Title id="sadp-modal-title-1">
                        {isOffered ? 'Change Offer' : 'Extend Offer'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group controlId="sadp-select-a-course">
                        <Form.Label>Select a course</Form.Label>
                        <Form.Select
                            aria-label="Select course for offer"
                            value={selectedCourseId}
                            onChange={(e) =>
                                setSelectedCourseId(e.target.value)
                            }
                        >
                            <option value="">Select a course...</option>
                            {courses.map((course) => (
                                <option key={course._id} value={course._id}>
                                    {course.subject} {course.courseNumber}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="outline-primary"
                        onClick={() => setShowOfferModal(false)}
                        disabled={pending !== null}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="success"
                        onClick={async () => {
                            const succeeded = await handleStatusChange(
                                'offered',
                                selectedCourseId
                            );
                            if (succeeded) {
                                setShowOfferModal(false);
                            }
                        }}
                        disabled={pending !== null || !selectedCourseId}
                    >
                        {pending === 'offered' ? 'Updating...' : 'Confirm'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <RejectStudentApplicationModal
                show={confirmingReject}
                takesBackOffer={isOffered}
                studentName={application.student?.name}
                programLabel={
                    PROGRAM_NAMES[application.program] ||
                    application.program?.toUpperCase()
                }
                term={termName(application.term)}
                offeredCourseLabel={
                    application.offeredCourse
                        ? `${application.offeredCourse.subject} ${application.offeredCourse.courseNumber}`
                        : undefined
                }
                onCancel={() => setConfirmingReject(false)}
                onConfirm={() => handleStatusChange('rejected')}
            />
        </Container>
    );
}

export default StudentApplicationDetailPage;
